import { useState } from 'react'
import { fetchSSE, runJDMatches } from '../utils/api'
import { API_CONFIG } from '../config/constants'
import topicDecisionEngine, {
  getCustomComponent
} from '../utils/topicDecisionEngine'

/**
 * Custom hook for handling messaging functionality
 * @param {Object} params - Parameters for the hook
 * @param {Function} params.setMessages - Function to update messages state
 * @param {Array} params.messages - Current messages
 * @param {string} params.currentSessionId - Current session ID
 * @param {string} params.userId - User ID
 * @param {Array} params.queries - Search queries
 * @param {Array} params.selectedRoleTypes - Selected role types
 * @param {Function} params.handleSSEMessage - Function to handle SSE messages
 * @param {boolean} params.isAudioReady - Whether audio is ready to play
 * @param {Function} params.saveCurrentSession - Function to save the current session
 * @param {Object} params.currentDiscoveryState - Current discovery state
 * @param {Function} params.advanceToNextTopic - Function to advance to the next topic
 * @param {Function} params.handleRefreshResults - Function to refresh match results
 * @param {Object} params.matchResults - Match results from useMatchResults
 * @param {Array} params.icpRequirements - Centralized ICP requirements
 * @param {Function} params.updateRequirements - Function to update centralized ICP requirements
 * @returns {Object} Messaging state and functions
 */
const useMessaging = ({
  setMessages,
  messages,
  currentSessionId,
  userId,
  queries,
  selectedRoleTypes,
  handleRoleTypeSelect,
  handleSSEMessage,
  isAudioReady,
  saveCurrentSession,
  currentDiscoveryState,
  advanceToNextTopic,
  handleRefreshResults,
  matchResults,
  icpRequirements,
  updateRequirements,
  getAllRequirements, // Add parameter to get all ICP requirements
  forcePythonBackend = false, // Add parameter to force Python backend state
  discoveryProgress = null, // Add discovery progress parameter
  startTypingTimeout = null, // Add timeout function for stuck typing states
  clearTypingTimeout = null // Add function to clear timeout
}) => {
  const [usePythonBackend, setUsePythonBackend] = useState(forcePythonBackend)
  const [experienceHistogramData, setExperienceHistogramData] = useState(null)
  const [levelCounts, setLevelCounts] = useState(null)
  const [locationMap, setLocationMap] = useState(null)
  const [locationKeys, setLocationKeys] = useState([])
  const [titleKeys, setTitleKeys] = useState([]) // Add state for titleKeys
  const [companyStageData, setCompanyStageData] = useState(null) // Add state for companyStageData
  const [companyBusinessModelData, setCompanyBusinessModelData] = useState(null) // Add state for companyStageData
  const [selectedSkills, setSelectedSkills] = useState([]) // Add state for selectedSkills
  const [confirmedSkills, setConfirmedSkills] = useState([]) // Add state for confirmed skills

  /**
   * Send a message to the server using SSE
   * @param {Object} payload - Message payload
   */
  const sendToServerSSE = async payload => {
    try {
      // Add dummy "Typing..." message
      if (
        !messages.some(msg => msg.role === 'AI' && msg.content === 'Typing...')
      ) {
        setMessages(prev => [
          ...prev,
          {
            role: 'AI',
            content: 'Typing...',
            thinking: 'Analyzing your request and formulating a response...\n',
            showThinking: true
          }
        ])
        
        // Start timeout to prevent stuck typing state
        if (startTypingTimeout) {
          startTypingTimeout();
        }
      }
      // Log the current session state
      console.log(`Sending message with currentSessionId: ${currentSessionId}`)
      console.log(`Current messages count: ${messages.length}`)

      let combinedMessage = payload.text
      let updatedDiscoveryState = currentDiscoveryState

      // Only use topic decision engine if not using Python backend and not processing a JD URL
      if (
        !usePythonBackend &&
        !payload.skipTopicDecisionEngine
        // !(typeof window !== 'undefined' && window.__jdInFlight === true)
      ) {
        try {
          // Log the input to the topic decision engine
          console.log('TOPIC DECISION ENGINE - INPUT:', {
            userMessage: payload.text,
            messagesCount: messages.length,
            currentTopic: currentDiscoveryState?.currentDiscoTopic || 'unknown',
            discoveryState: currentDiscoveryState
          })
          // Process the user message using the topic decision engine
          const decision = await topicDecisionEngine.processUserMessage(
            payload.text,
            messages,
            currentDiscoveryState,
            advanceToNextTopic,
            queries,
            setMessages,
            handleSendText, // Pass handleSendText to the topic decision engine
            updateRequirements, // Pass updateRequirements to the topic decision engine
            getAllRequirements, // Pass the actual getAllRequirements function from useICPRequirements
            payload.isJdUrl, // Pass isJdUrl flag
            setTitleKeys, // Pass setTitleKeys to the topic decision engine
            setConfirmedSkills, // Pass setConfirmedSkills to the topic decision engine
            discoveryProgress, // Pass discovery progress to the topic decision engine
            currentSessionId // Pass current session ID to the topic decision engine
          )

          // Log the detailed decision result
          console.log('TOPIC DECISION ENGINE - RESULT:', {
            actionType: decision.actionType,
            currentTopic: decision.currentTopic,
            strategyPhase: decision.strategyPhase,
            agentGuidancePreview: decision.agentGuidance
              ? decision.agentGuidance.substring(0, 100) + '...'
              : null,
            updatedDiscoveryState: decision.updatedDiscoveryState
          })

          // Add more detailed logging for debugging the strategy phase
          console.log('STRATEGY PHASE DEBUG:', {
            incomingStrategyPhase: currentDiscoveryState?.strategyPhase,
            decisionStrategyPhase: decision.strategyPhase,
            currentTopic: currentDiscoveryState?.currentDiscoTopic,
            actionType: decision.actionType
          })

          // Always use the updated discovery state from the decision if available
          if (decision.updatedDiscoveryState) {
            // Store the strategy phase and actionType in the discovery state to preserve it between messages
            updatedDiscoveryState = {
              ...decision.updatedDiscoveryState,
              strategyPhase:
                decision.strategyPhase ||
                decision.updatedDiscoveryState.strategyPhase,
              actionType: decision.actionType // Store the actionType in the discovery state
            }
            console.log(
              'Using updated discovery state from decision with strategyPhase and actionType:',
              updatedDiscoveryState
            )
          } else if (decision.strategyPhase || decision.actionType) {
            // If there's no updated discovery state but there is a strategy phase or actionType, update the current discovery state
            updatedDiscoveryState = {
              ...currentDiscoveryState,
              strategyPhase:
                decision.strategyPhase || currentDiscoveryState?.strategyPhase,
              actionType: decision.actionType // Store the actionType in the discovery state
            }
            console.log(
              'Updating current discovery state with strategyPhase and actionType:',
              updatedDiscoveryState
            )
          }

          // Special case: If the companies topic returned CONTINUE_DISCOVERY, don't send to backend
          if (
            decision.actionType === 'CONTINUE_DISCOVERY' &&
            currentDiscoveryState?.currentDiscoTopic === 'companies'
          ) {
            console.log(
              'Companies topic handled by companyQueueStrategy with CONTINUE_DISCOVERY, not sending to backend'
            )

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the education topic returned CONTINUE_DISCOVERY, don't send to backend
          else if (
            decision.actionType === 'CONTINUE_DISCOVERY' &&
            currentDiscoveryState?.currentDiscoTopic === 'education'
          ) {
            console.log(
              'Education topic handled by educationQueueStrategy with CONTINUE_DISCOVERY, not sending to backend'
            )

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the business model topic returned CONTINUE_DISCOVERY, don't send to backend
          else if (
            decision.actionType === 'CONTINUE_DISCOVERY' &&
            (currentDiscoveryState?.currentDiscoTopic === 'business_model' ||
              currentDiscoveryState?.currentDiscoTopic === 'company_biz_model')
          ) {
            console.log(
              'Business model topic handled by businessModelQueueStrategy with CONTINUE_DISCOVERY, not sending to backend'
            )

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the company size topic returned CONTINUE_DISCOVERY, don't send to backend
          else if (
            decision.actionType === 'CONTINUE_DISCOVERY' &&
            currentDiscoveryState?.currentDiscoTopic === 'company_size'
          ) {
            console.log(
              'Company size topic handled by companySizeQueueStrategy with CONTINUE_DISCOVERY, not sending to backend'
            )

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is for rendering a custom component, don't send to backend
          else if (
            decision.actionType === 'RENDER_EXPERIENCE_HISTOGRAM' ||
            decision.actionType === 'RENDER_EXPERIENCE_OPTIONS' ||
            decision.actionType === 'RENDER_KEYWORDS_SELECTION'
          ) {
            console.log(
              `${decision.actionType} component is being rendered, not sending to backend`
            )
            
            // Special logging for keywords
            if (decision.actionType === 'RENDER_KEYWORDS_SELECTION') {
              console.log('🎯🎯🎯 KEYWORDS SELECTION COMPONENT SHOULD BE RENDERED!!!');
              console.log('🎯 Keywords componentProps:', decision.componentProps);
            }

            // Check if we should show a custom component
            const componentDecision = getCustomComponent(decision)
            if (componentDecision.showCustomComponent) {
              console.log(
                'Should show experience histogram component:',
                componentDecision
              )
            }

            // Store the experienceHistogram data directly in state
            if (
              decision.aiMatchesResult &&
              decision.aiMatchesResult.experienceHistogram
            ) {
              console.log(
                'Storing experienceHistogram data directly:',
                decision.aiMatchesResult.experienceHistogram
              )

              // Set the experienceHistogram data in state
              setExperienceHistogramData(
                decision.aiMatchesResult.experienceHistogram
              )
            }

            if (
              decision.aiMatchesResult &&
              decision.aiMatchesResult.levelCounts
            ) {
              console.log(
                'Storing levelCounts data directly:',
                decision.aiMatchesResult.levelCounts
              )

              // Set the levelCounts data in state
              setLevelCounts(decision.aiMatchesResult.levelCounts)
            }

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is DIRECT_TYPO_RESPONSE, add an AI message directly without sending to backend
          else if (decision.actionType === 'DIRECT_TYPO_RESPONSE') {
            console.log(
              'Direct typo response detected, adding AI message without sending to backend'
            )

            // Remove the "Typing..." message
            setMessages(prev => {
              // First filter out the "Typing..." message
              const withoutTyping = prev.filter(
                msg => msg.content !== 'Typing...'
              )

              // Then add the new AI message with the clarification
              return [
                ...withoutTyping,
                {
                  role: 'AI',
                  content: decision.clarificationMessage,
                  id: `ai_${Date.now()}`
                }
              ]
            })

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is DIRECT_COPILOT_RESPONSE, add copilot clarifying question directly without sending to backend
          else if (decision.actionType === 'DIRECT_COPILOT_RESPONSE') {
            console.log(
              'Direct copilot response detected, adding AI message without sending to backend'
            )

            // Clear any existing timeout since we're providing a response
            if (clearTypingTimeout) {
              clearTypingTimeout();
            }

            // Remove the "Typing..." message
            setMessages(prev => {
              // First filter out the "Typing..." message
              const withoutTyping = prev.filter(
                msg => msg.content !== 'Typing...'
              )

              // Then add the new AI message with the copilot clarifying question
              return [
                ...withoutTyping,
                {
                  role: 'AI',
                  content: decision.clarificationMessage,
                  id: `ai_copilot_${Date.now()}`
                }
              ]
            })

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is RENDER_LOCATION_KEYS, don't send to backend
          else if (decision.actionType === 'RENDER_LOCATION_KEYS') {
            console.log(
              'Location keys component is being rendered, not sending to backend'
            )

            // Check if we should show a custom component
            const componentDecision = getCustomComponent(decision)
            if (componentDecision.showCustomComponent) {
              console.log(
                'Should show location keys component:',
                componentDecision
              )
            }

            // Store the location data directly in state
            if (decision.locationData) {
              console.log(
                'Storing location data directly:',
                decision.locationData
              )

              // Set the location map and keys in state
              setLocationMap(decision.locationData.locationMap || {})
              setLocationKeys(decision.locationData.locationKeys || [])
            }

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is RENDER_TITLE_DISTRIBUTION, don't send to backend
          else if (decision.actionType === 'RENDER_TITLE_DISTRIBUTION') {
            console.log(
              'Title distribution component is being rendered, not sending to backend'
            )

            // Check if we should show a custom component
            const componentDecision = getCustomComponent(decision)
            if (componentDecision.showCustomComponent) {
              console.log(
                'Should show title distribution component:',
                componentDecision
              )
            }

            // Store the title keys directly in state
            if (decision.componentProps && decision.componentProps.titleKeys) {
              console.log(
                'Storing title keys directly:',
                decision.componentProps.titleKeys.length
              )

              // Set the title keys in state
              setTitleKeys(decision.componentProps.titleKeys || [])
            }

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is RENDER_TITLE_OPTIONS, don't send to backend
          else if (decision.actionType === 'RENDER_TITLE_OPTIONS') {
            console.log(
              'Title options component is being rendered, not sending to backend'
            )

            // Check if we should show a custom component
            const componentDecision = getCustomComponent(decision)
            if (componentDecision.showCustomComponent) {
              console.log(
                'Should show title options component:',
                componentDecision
              )
            }

            // Store the title keys directly in state
            if (decision.componentProps && decision.componentProps.titleKeys) {
              console.log(
                'Storing title keys directly:',
                decision.componentProps.titleKeys.length
              )

              // Set the title keys in state
              setTitleKeys(decision.componentProps.titleKeys || [])
            }

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is RENDER_COMPANY_STAGE_OPTIONS, don't send to backend
          else if (decision.actionType === 'RENDER_COMPANY_STAGE_OPTIONS') {
            console.log(
              'Company stage options component is being rendered, not sending to backend'
            )

            // Check if we should show a custom component
            const componentDecision = getCustomComponent(decision)
            if (componentDecision.showCustomComponent) {
              console.log(
                'Should show company stage options component:',
                componentDecision
              )
            }

            // Store the company stage data directly in state
            if (
              decision.aiMatchesResult &&
              decision.aiMatchesResult.companyStageData
            ) {
              console.log(
                'Storing company stage data directly:',
                decision.aiMatchesResult.companyStageData
              )

              // Set the company stage data in state
              setCompanyStageData(decision.aiMatchesResult.companyStageData)
            }

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          } else if (
            decision.actionType === 'RENDER_COMPANY_BIZMODEL_OPTIONS'
          ) {
            console.log(
              'Company business model options component is being rendered, not sending to backend'
            )

            // Check if we should show a custom component
            const componentDecision = getCustomComponent(decision)
            if (componentDecision.showCustomComponent) {
              console.log(
                'Should show company bizmodel options component:',
                componentDecision
              )
            }

            // Store the company stage data directly in state
            if (
              decision.aiMatchesResult &&
              decision.aiMatchesResult.businessModelData
            ) {
              console.log(
                'Storing company bizmodel data directly:',
                decision.aiMatchesResult.businessModelData
              )

              // Set the company stage data in state
              setCompanyBusinessModelData(
                decision.aiMatchesResult.businessModelData
              )
            }

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }
          // Special case: If the action type is RENDER_SKILLS_CONFIRMATION, don't send to backend
          else if (decision.actionType === 'RENDER_SKILLS_CONFIRMATION') {
            console.log(
              'Skills confirmation component is being rendered, not sending to backend'
            )

            // Check if we should show a custom component
            const componentDecision = getCustomComponent(decision)
            if (componentDecision.showCustomComponent) {
              console.log(
                'Should show skills confirmation component:',
                componentDecision
              )
            }

            // Store the skills data directly in state
            if (decision.componentProps && decision.componentProps.skills) {
              console.log(
                'Storing skills data directly:',
                decision.componentProps.skills
              )

              // Set the skills data in state
              setSelectedSkills(decision.componentProps.skills || [])
            }

            // Remove the "Typing..." message since we're not going to get a response
            setMessages(prev => prev.filter(msg => msg.content !== 'Typing...'))

            // Don't proceed with sending to the backend
            return
          }

          // Special case: If the action type is USER_REJECTED_TITLE_SUGGESTIONS, reset titleKeys
          else if (decision.actionType === 'USER_REJECTED_TITLE_SUGGESTIONS') {
            console.log('User rejected title suggestions, resetting titleKeys')

            // Reset the title keys to an empty array
            setTitleKeys([])
          }

          // Update the message based on the decision
          if (decision.actionType === 'ADVANCE_TOPIC') {
            // User confirmed to proceed to the next topic
            payload.userConfirmedToAdvance = true
            if (decision.agentGuidance) {
              console.log(
                'ADDING AGENT GUIDANCE (ADVANCE_TOPIC):',
                decision.agentGuidance.substring(0, 100) + '...'
              )
              combinedMessage = `${payload.text}\n\n[AGENT GUIDANCE: ${decision.agentGuidance}]`
            }
          } else if (decision.actionType === 'CHANGE_TOPIC') {
            // User implicitly changed the topic
            payload.userChangedTopic = true
            payload.previousTopic = decision.previousTopic
            payload.newTopic = decision.newTopic
            if (decision.agentGuidance) {
              console.log(
                'ADDING AGENT GUIDANCE (CHANGE_TOPIC):',
                decision.agentGuidance.substring(0, 100) + '...'
              )
              combinedMessage = `${payload.text}\n\n[AGENT GUIDANCE: ${decision.agentGuidance}]`
            }
          } else if (decision.actionType === 'SEND_WITH_GUIDANCE') {
            // Add agent guidance to the message
            if (decision.agentGuidance) {
              console.log(
                'ADDING AGENT GUIDANCE (SEND_WITH_GUIDANCE):',
                decision.agentGuidance.substring(0, 100) + '...'
              )
              combinedMessage = `${payload.text}\n\n[AGENT GUIDANCE: ${decision.agentGuidance}]`
            }

            // Call handleRefreshResults to update the UI state (don't wait for it)
            // This will update matchCount which is needed for the IntakeSummary component
            if (handleRefreshResults && decision.aiMatchesResult) {
              const messagesWithCurrentText = [
                ...messages,
                { role: 'User', content: payload.text }
              ]
              handleRefreshResults(messagesWithCurrentText)
            }
          } else if (decision.agentGuidance) {
            // For any other action type, if there's agent guidance, add it
            console.log(
              'ADDING AGENT GUIDANCE (OTHER):',
              decision.agentGuidance.substring(0, 100) + '...'
            )
            combinedMessage = `${payload.text}\n\n[AGENT GUIDANCE: ${decision.agentGuidance}]`
          }

          // Check if we should show a custom component
          const componentDecision = getCustomComponent(decision)
          if (componentDecision.showCustomComponent) {
            // Handle custom component display
            console.log('Should show custom component:', componentDecision)
            // This would be implemented in the UI layer
          }
        } catch (err) {
          console.error('Error in topic decision engine:', err)
          // Continue with original message if decision engine fails
        }
      }

      payload.text = combinedMessage

      // Log that we're about to send the SSE request with session context
      if (currentSessionId) {
        console.log(`Sending SSE request with sessionId: ${currentSessionId}`)
      } else {
        console.log('Sending SSE request without sessionId (new conversation)')
      }

      // Create a complete payload with all necessary data
      const completePayload = {
        ...payload,
        messages: messages || [], // Ensure messages is always defined
        useServerChatHistory: currentSessionId ? true : false // Use server chat history if we have a session ID
      }

      // Log the complete payload for debugging
      console.log('COMPLETE PAYLOAD:', {
        type: completePayload.type,
        text: completePayload.text
          ? completePayload.text.substring(0, 100) + '...'
          : null,
        userConfirmedToAdvance: completePayload.userConfirmedToAdvance,
        userChangedTopic: completePayload.userChangedTopic,
        previousTopic: completePayload.previousTopic,
        newTopic: completePayload.newTopic,
        discoveryState: completePayload.discoveryState
          ? {
              roleType: completePayload.discoveryState.roleType,
              currentTopic: completePayload.discoveryState.currentDiscoTopic,
              completedTopics: completePayload.discoveryState.completedTopics,
              discoveryOrder: completePayload.discoveryState.discoveryOrder,
              strategyPhase: completePayload.discoveryState.strategyPhase // Include strategyPhase in logging
            }
          : null
      })

      // Add role types to the payload if this is the first message and we have selected role types
      if (messages.length === 0 && selectedRoleTypes.length > 0) {
        completePayload.roleTypes = selectedRoleTypes
      }

      // Add discovery state to the payload if it exists
      if (updatedDiscoveryState) {
        completePayload.discoveryState = updatedDiscoveryState
        console.log(`Adding discovery state to payload:`, updatedDiscoveryState)
      }

      await fetchSSE(
        `${API_CONFIG.url}/api/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(completePayload)
        },
        handleSSEMessage,
        isAudioReady,
        currentSessionId,
        userId,
        payload.tempMessageId,
        0, // retryCount
        3, // maxRetries
        usePythonBackend // Use Python backend if enabled
      )
    } catch (err) {
      console.error('SSE request failed:', err)
    }
  }

  /**
   * Send a text message
   * @param {string} text - Text message to send
   */
  const handleSendText = async input => {
    const isObjectInput =
      input &&
      typeof input === 'object' &&
      Object.prototype.hasOwnProperty.call(input, 'text')
    const options = isObjectInput ? input : {}
    const text = isObjectInput ? input.text : input
    const skipRenderUserBubble = !!options.skipRenderUserBubble
    const skipTopicDecisionEngine = !!options.skipTopicDecisionEngine
    const tempMessageId = `temp_${Date.now()}`

    // Only check for duplicates if the last message was from the same user with the same content
    // This prevents legitimate re-sends but allows for normal conversation flow
    const lastMessage = messages[messages.length - 1]
    const isImmediateDuplicate =
      lastMessage && lastMessage.role === 'User' && lastMessage.content === text

    if (isImmediateDuplicate) {
      console.log('Skipping immediate duplicate user message:', text)
      return // Don't add the message or send it to the server
    }

    // Check if the message contains a URL
    const urlRegex = /(https?:\/\/[^\s]+)/g
    const urls = text.match(urlRegex)

    // Add the user message to the messages array (unless instructed to skip rendering)
    if (!skipRenderUserBubble) {
      setMessages(prev => {
        const updatedMessages = [
          ...prev,
          { role: 'User', content: text, id: tempMessageId }
        ]
        // Disable automatic session saving here to prevent duplicate sessions
        // Sessions will only be saved when a role type is selected in useRoleTypeManager
        return updatedMessages
      })
    }

    // Flag to track if we're processing a JD URL
    let isProcessingJdUrl = false
    let jdRequirements = []

    if (urls && urls.length > 0) {
      console.log('Detected URLs in message:', urls)

      // Add a single "Typing..." message to show we're processing the URL
      setMessages(prev => {
        if (
          !prev.some(msg => msg.role === 'AI' && msg.content === 'Typing...')
        ) {
          return [
            ...prev,
            {
              role: 'AI',
              content: 'Typing...',
              thinking: 'Analyzing job description URL...\n',
              showThinking: true
            }
          ]
        }
        return prev
      })

      try {
        // Process the first URL as a job description
        const jdUrl = urls[0]
        console.log('Processing job description URL:', jdUrl)
        isProcessingJdUrl = true

        // Call runJDMatches to get ICP requirements from the job description
        const jdResult = await runJDMatches(jdUrl)

        // Check if we got valid results with ICP requirements
        if (
          jdResult &&
          jdResult.search_gen_response &&
          jdResult.search_gen_response.icp_requirements &&
          Array.isArray(jdResult.search_gen_response.icp_requirements)
        ) {
          // Extract ICP requirements from the result
          jdRequirements = jdResult.search_gen_response.icp_requirements
          console.log(
            'Extracted ICP requirements from job description:',
            jdRequirements.length
          )

          // Update centralized ICP requirements if updateRequirements is available
          if (updateRequirements) {
            console.log(
              'Updating centralized ICP requirements with JD requirements:',
              jdRequirements.length
            )

            // Log the first few requirements for debugging
            if (jdRequirements.length > 0) {
              console.log(
                'Sample JD requirements:',
                jdRequirements.slice(0, 3).map(req => ({
                  section_name: req.section_name,
                  bool_str: req.bool_str
                    ? req.bool_str.substring(0, 50) + '...'
                    : 'undefined'
                }))
              )
            }

            // Update the central ICP requirements
            updateRequirements(jdRequirements, 'job_description')

            // Verify that getAllRequirements returns the updated requirements
            if (getAllRequirements) {
              const reqs = getAllRequirements()
              console.log(
                'getAllRequirements returns:',
                reqs.length,
                'requirements after JD update'
              )
            }

            // Verify that getAllRequirements returns the updated requirements
            if (getAllRequirements) {
              const reqs = getAllRequirements()
              console.log(
                'getAllRequirements returns:',
                reqs.length,
                'requirements after JD update'
              )
            }
          } else {
            console.error('updateRequirements function is not available')
          }

          // Update thinking accordion with JD requirements
          setMessages(prev => {
            const updatedMessages = prev.map(msg =>
              msg.role === 'AI' && msg.content === 'Typing...'
                ? {
                    ...msg,
                    thinking: `${msg.thinking}\nExtracted ${jdRequirements.length} requirements from job description URL.`
                  }
                : msg
            )
            return updatedMessages
          })
        }
      } catch (error) {
        console.error('Error processing job description URL:', error)

        // Update thinking accordion with error
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.role === 'AI' && msg.content === 'Typing...'
              ? {
                  ...msg,
                  thinking: `${msg.thinking}\nError processing job description URL: ${error.message}`
                }
              : msg
          )
          return updatedMessages
        })
      }
    }

    // Ensure role type/discovery order for JD pastes (URL or JD-looking text)
    try {
      const jdHeuristic =
        !isProcessingJdUrl &&
        typeof text === 'string' &&
        text.length > 400 &&
        /(responsibilit|requirement|qualification|job description|about the role|what you will)/i.test(
          text
        )
      if (isProcessingJdUrl || jdHeuristic) {
        if (typeof window !== 'undefined') {
          // Route subsequent AI matches through JD path
          try {
            window.__intakeChoice = 'jd'
            localStorage.setItem('intakeChoice', 'jd')
          } catch (_) {}
          // Ensure discovery role type is initialized to TECHNICAL and discovery order set
          if (typeof handleRoleTypeSelect === 'function') {
            await handleRoleTypeSelect(['TECHNICAL'], { silent: true })
          } else if (
            typeof window !== 'undefined' &&
            typeof window.ensureTechnicalRoleType === 'function'
          ) {
            window.ensureTechnicalRoleType()
          }
          // Gate queue/UI until JD SearchGen returns
          window.__jdInFlight = true
        }
        // Proactively start JD SearchGen path so discovery order is consistent with TECHNICAL
        try {
          if (typeof handleRefreshResults === 'function') {
            const messagesWithCurrentText = [
              ...messages,
              { role: 'User', content: text }
            ]
            handleRefreshResults(messagesWithCurrentText, undefined, {
              intakeType: 'jd'
            })
          }
        } catch (e) {
          console.warn('JD proactive refresh failed:', e?.message || e)
        }
      }
    } catch (_e) {}

    // Even if we're processing a JD URL, we should still run the topic decision engine
    // But we'll pass a flag to indicate it's a JD URL so strategies can use central ICP requirements
    sendToServerSSE({
      type: 'text',
      text,
      tempMessageId,
      isJdUrl: isProcessingJdUrl,
      skipTopicDecisionEngine
    })
  }

  /**
   * Handle transcription completion from audio recording
   * @param {string} transcribedText - Transcribed text
   * @param {string} tempMessageId - Temporary message ID
   */
  const handleTranscriptionComplete = (transcribedText, tempMessageId) => {
    // Replace temporary message with the transcribed text
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.id === tempMessageId ? { ...msg, content: transcribedText } : msg
      )

      // Disable automatic session saving here to prevent duplicate sessions
      // Sessions will only be saved when a role type is selected in useRoleTypeManager
      return updatedMessages
    })

    // Send the transcribed text to the server
    sendToServerSSE({ type: 'text', text: transcribedText, tempMessageId })
  }

  return {
    usePythonBackend,
    setUsePythonBackend,
    handleSendText,
    handleTranscriptionComplete,
    experienceHistogramData,
    levelCounts,
    locationMap,
    locationKeys,
    titleKeys, // Add titleKeys to the returned object
    setTitleKeys, // Add setTitleKeys to the returned object
    companyStageData, // Add companyStageData to the returned object
    setCompanyStageData, // Add setCompanyStageData to the returned object
    selectedSkills, // Add selectedSkills to the returned object
    setSelectedSkills, // Add setSelectedSkills to the returned object
    confirmedSkills, // Add confirmedSkills to the returned object
    setConfirmedSkills // Add setConfirmedSkills to the returned object
  }
}

export default useMessaging
